<template>
  <div v-if="type === 'input'" class="relative flex flex-col">
    <CalendarIcon
      v-if="element.dataType === 'DATE'"
      :style="{ top: iconTopPosition }"
      :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
    />
    <Mail01Icon
      v-if="element.dataType === 'EMAIL'"
      :style="{ top: iconTopPosition }"
      :class="`input-icon pointer-events-none absolute left-[12px] z-[1] h-[16px] !w-[16px] -translate-y-1/2`"
    />
    <input
      ref="inputElement"
      :value="inputValue"
      :placeholder="inputPlaceholder"
      class="form-placeholder form-control shadow-none focus:outline-transparent focus:ring-0"
      :class="classList"
      :style="{
        ...styleObject,
        ...(isIconSupported ? { paddingLeft: '36px' } : {}),
      }"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="$t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @input="handleInputShortLabel"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">
        {{ element.shortLabel }}
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import {
  fixSafariCursor,
  handleShortLabelInput as handleShortLabelInputUtil,
} from "@/util/methods";
import { CalendarIcon, Mail01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { computed, nextTick, onMounted, ref, watch } from "vue";

const props = defineProps({
  element: {
    type: Object,
    default: () => {
      return {};
    },
  },
  type: {
    type: String,
    default: "input",
  },
  placeholder: { type: String, default: "" },
  classList: {
    type: Array,
    default: () => {
      return [];
    },
  },
  styleObject: {
    type: Object,
    default: () => {
      return {
        fontSize: "14px",
      };
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const iconTopPosition = ref<string>("50%");
const store = useAppStore();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();
const emit = defineEmits(["update:placeholder", "update:shortLabel"]);
const inputPlaceholder = ref(props.placeholder);
const inputValue = ref("");
const shortLabel = ref(props.element.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const inputElement = ref<HTMLInputElement | null>(null);

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const isIconSupported = computed(() => {
  return props.element.dataType === "EMAIL" || props.element.dataType === "DATE";
});

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const updateIconPosition = () => {
  nextTick(() => {
    if (inputElement.value) {
      const inputHeight = inputElement.value.offsetHeight;
      const centerPosition = inputHeight / 2;
      iconTopPosition.value = `${centerPosition}px`;
    }
  });
};

// Watch for input height changes
watch(
  () => [props.styleObject, props.classList, inputElement.value],
  () => {
    updateIconPosition();
  },
  { deep: true }
);

// Watch for isActive changes (when short label appears/disappears)
watch(
  () => props.isActive,
  () => {
    updateIconPosition();
  }
);

onMounted(() => {
  updateIconPosition();
});

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  inputPlaceholder.value = target.value;
  emit("update:placeholder", inputPlaceholder.value);
};

const handleFocus = () => {
  inputValue.value = inputPlaceholder.value;
  nextTick(() => {
    try {
      inputElement.value?.select?.();
    } catch (error) {
      console.error("Failed to select placeholder text on focus", error);
    }
  });
};

const handleBlur = (event: Event) => {
  inputValue.value = "";
  store.addToHistory();
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);
</script>

<style scoped>
.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}
</style>
